import styles from './barStyles.module.scss';
import { Panel } from 'react-resizable-panels';
import { ElementType, FC, useCallback } from 'react';
import { Resolver, useEditor } from '@craftjs/core';
import { Layers } from '@craftjs/layers';
import { SelectorGroupEnum, SelectorResolverType } from '../generalFormBuilder.t';
import { PanelContainer } from './panels/uiKit/panelContainer/panelContainer';
import { ControlsGroup } from './panels/uiKit/controlsGroup/controlsGroup';
import { ControlSelectorItem } from 'src/components/formEditor/toolbox/components/controlSelectorItem';
import { BufferPanel } from 'src/components/formEditor/toolbox/bufferPanel/bufferPanel';
import { ExpandedPanelsContextProvider } from './settingsPanel/settingsPanelContextProvider';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { Divider } from 'src/components/formEditor/toolbox/components/divider';

type SelectorGroupWithTitle = {
  group: SelectorGroupEnum;
  title?: string;
};

export const LeftBar: FC<{
  selectors: SelectorResolverType<Resolver>;
  layerItem: ElementType<any> | undefined;
}> = ({ selectors, layerItem }) => {
  const { t } = useScopedTranslation('GeneralFormBuilder.Panels');
  const { query } = useEditor();

  const renderSelectorsByGroup = useCallback(
    (groups: SelectorGroupWithTitle[]) =>
      groups.map((x, i) => {
        const filteredItems = Object.entries(selectors)
          .filter(([, value]) => x.group === value.group)
          .map(([, selector]) => (
            <ControlSelectorItem
              {...selector}
              key={selector.name}
              component={query.parseReactElement(selector.userComponent).toNodeTree()}
            />
          ));

        if (!x.title) return filteredItems;

        return (
          <ControlsGroup header={x.title} key={i}>
            {filteredItems}
          </ControlsGroup>
        );
      }),
    [query, selectors],
  );

  return (
    <Panel minSize={15} maxSize={50} defaultSize={15} className={styles.toolboxContainer}>
      <ExpandedPanelsContextProvider>
        <PanelContainer variant={'flex'} header={t('Controls.Header')} expanded={true} panelName={'Controls'}>
          {renderSelectorsByGroup([
            {
              group: SelectorGroupEnum.InputFields,
              title: t('Controls.InputFields'),
            },
            {
              group: SelectorGroupEnum.Time,
              title: t('Controls.Time'),
            },
            {
              group: SelectorGroupEnum.SingleChoice,
              title: t('Controls.SingleChoice'),
            },
            {
              group: SelectorGroupEnum.MultipleChoice,
              title: t('Controls.MultipleChoice'),
            },
            {
              group: SelectorGroupEnum.OtherInput,
              title: t('Controls.OtherInput'),
            },
          ])}
          <Divider />
          {renderSelectorsByGroup([{ group: SelectorGroupEnum.Containers }])}
          <Divider />
          {renderSelectorsByGroup([{ group: SelectorGroupEnum.Content }])}
        </PanelContainer>

        <PanelContainer variant={'flex'} header={t('Layers.Header')} expanded={false} panelName={'Layers'}>
          <Layers expandRootOnLoad={true} renderLayer={layerItem} />
        </PanelContainer>

        <PanelContainer variant={'flex'} header={t('Buffer.Header')} expanded={false} panelName={'Buffer'}>
          <BufferPanel selectors={selectors} />
        </PanelContainer>
      </ExpandedPanelsContextProvider>
    </Panel>
  );
};
